import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, ButtonBase, CircularProgress } from "@mui/material";
import UserChart from "./UserChart";
import FeedChart from "./FeedChart";
import usePermission from "../../hooks/usePermission";
import { admin_base_url } from "../../Global";

const StatCard = ({ label, value, color, loading }) => (
  <Box style={{
    flex: 1, minWidth: "140px",
    background: "#111827", borderRadius: "12px",
    border: "1px solid #1f2937", padding: "20px 24px",
    display: "flex", flexDirection: "column", gap: "8px",
  }}>
    <Typography style={{ color: "#6b7280", fontSize: "12px", fontFamily: "Gilroy, sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
      {label}
    </Typography>
    {loading ? (
      <CircularProgress size={20} sx={{ color }} />
    ) : (
      <Typography style={{ color, fontSize: "28px", fontWeight: 700, fontFamily: "Gilroy, sans-serif" }}>
        {value ?? "—"}
      </Typography>
    )}
  </Box>
);

const DataAnalysis = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = usePermission();
  const [admins, setAdmins] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  const token = localStorage.getItem("accessToken");

  const firstName = user?.first_name || user?.firstName || "";
  const lastName  = user?.last_name  || user?.lastName  || "";
  const fullName  = [firstName, lastName].filter(Boolean).join(" ");
  const displayName = fullName || user?.name || user?.username || user?.email?.split("@")[0] || "Admin";
  const displayRole = user?.role || "admin";

  useEffect(() => {
    if (!isSuperAdmin) { setStatsLoading(false); return; }
    const fetchAdmins = async () => {
      if (!token) return;
      try {
        const res = await fetch(admin_base_url + "/all", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ superAdminId: user?._id }),
        });
        const data = await res.json();
        setAdmins(data.data || []);
      } catch (err) {
        console.error("Failed to fetch admin stats:", err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchAdmins();
  }, [token, isSuperAdmin]);

  const totalAdmins  = admins.length;
  const pending      = admins.filter((a) => a.status === "pending").length;
  const active       = admins.filter((a) => a.status === "active").length;
  const revoked      = admins.filter((a) => a.status === "revoked").length;

  return (
    <Box style={{ padding: "32px", display: "flex", flexDirection: "column", gap: "28px" }}>

      {/* ── Welcome header ─────────────────────────────────────────────────── */}
      <Box style={{
        background: "#111827", borderRadius: "16px",
        border: "1px solid #1f2937", padding: "28px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: "16px",
      }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "18px" }}>
          {/* Avatar */}
          <Box style={{
            width: "56px", height: "56px", borderRadius: "50%",
            background: "rgba(255,107,53,0.15)", color: "#FF6B35",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "22px", fontWeight: 700, fontFamily: "Gilroy, sans-serif",
            flexShrink: 0,
          }}>
            {displayName[0].toUpperCase()}
          </Box>

          <Box>
            <Typography style={{ color: "white", fontSize: "22px", fontWeight: 700, fontFamily: "Gilroy, sans-serif" }}>
              Welcome back, {displayName}
            </Typography>
            <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px", flexWrap: "wrap" }}>
              {firstName && (
                <Typography style={{ color: "#6b7280", fontSize: "13px", fontFamily: "Gilroy, sans-serif" }}>
                  {firstName}{lastName ? ` ${lastName}` : ""}
                </Typography>
              )}
              <Box style={{
                background: "rgba(255,107,53,0.15)", color: "#FF6B35",
                padding: "2px 10px", borderRadius: "999px",
                fontSize: "11px", fontWeight: 700, fontFamily: "Gilroy, sans-serif",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                {displayRole.replace(/_/g, " ")}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Quick action — super admin only */}
        {isSuperAdmin && (
          <ButtonBase
            onClick={() => navigate("/admin-management")}
            style={{
              background: "#FF6B35", color: "white",
              padding: "10px 22px", borderRadius: "8px",
              fontSize: "13px", fontFamily: "Gilroy, sans-serif", fontWeight: 700,
            }}
          >
            Manage Admins
          </ButtonBase>
        )}
      </Box>

      {/* ── Stat cards — super admin only ──────────────────────────────────── */}
      {isSuperAdmin && (
        <Box style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <StatCard label="Total Admins"  value={totalAdmins} color="white"    loading={statsLoading} />
          <StatCard label="Pending"       value={pending}     color="#facc15"  loading={statsLoading} />
          <StatCard label="Active"        value={active}      color="#4ade80"  loading={statsLoading} />
          <StatCard label="Revoked"       value={revoked}     color="#f87171"  loading={statsLoading} />
        </Box>
      )}

      {/* ── Pending approvals alert — super admin only ──────────────────────── */}
      {isSuperAdmin && !statsLoading && pending > 0 && (
        <Box style={{
          background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.25)",
          borderRadius: "10px", padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: "12px",
        }}>
          <Typography style={{ color: "#facc15", fontSize: "13px", fontFamily: "Gilroy, sans-serif" }}>
            You have <b>{pending}</b> admin account{pending > 1 ? "s" : ""} waiting for approval.
          </Typography>
          <ButtonBase
            onClick={() => navigate("/admin-management")}
            style={{
              color: "#facc15", fontSize: "12px", fontFamily: "Gilroy, sans-serif",
              fontWeight: 700, textDecoration: "underline",
            }}
          >
            Review now
          </ButtonBase>
        </Box>
      )}

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <Box style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
        <UserChart />
        <FeedChart />
      </Box>

    </Box>
  );
};

export default DataAnalysis;
